import { Component, ViewEncapsulation } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ButtonTriggerEvent } from '../../../common/components';
import { HubPlaceAdditionalData } from '../../../models/hub/hub.google.place.data';
import { HubPlace } from '../../../models/hub/hub.place';
import { HubVisitorToggleDataSource } from '../../../models/hub/hub.visitor.feature.toggle';
import { HubApiService } from '../../../services/hub.api.service';
import { ImageService } from '../../../services/image.service';
import { BaseComponent } from '../../../common/components/base.component';
import { MatDialogRef } from '@angular/material';
import { AlertifyService } from '../../../services/alertify.service';
import { PlaceService } from '../../../services/place.service';
import { ToggleAndDataSourceStore } from '../../../stores/hub.calendar.store';
import { EditPlaceImagesModalActions } from './edit.place.images.modal.provider';
import { ConfirmModalActions, ConfirmModalData } from '../../../common/components/confirm.modal/confirm.modal.provider';
import { ModalService } from '../../../common/services/modal.service';
import { ConfirmModalComponent } from '../../../common/components/confirm.modal/confirm.modal.component';
import { clone, isEmptyArray } from '../../../common/extensions';
import { config } from '../../../../config/config';
import { InputModalComponent } from "../../../common/components/input.modal/input.modal.component";
import { InputModalActions, InputModalData } from "../../../common/components/input.modal/input.modal.provider";

@Component({
  templateUrl: './edit.place.images.modal.component.html',
  styleUrls: ['./edit.place.images.modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EditPlaceImagesModalComponent extends BaseComponent {
  public place: HubPlace;
  public hubName: string;
  public googlePlaceAdditionalData: HubPlaceAdditionalData;
  public isGoogleInfoLoaded = false;
  public isDragging = false;
  public dragFrom = '';
  public dropTargets = [];

  public coverImage: string = '';
  public publishedImages: string[] = [];
  public compensateForPublished: any[] = [];

  public availableImages: string[] = [];
  public compensateForAvailable: any[] = [];

  public ownedImages: {
    url: string;
    status: 'initial' | 'uploading' | 'uploaded' | 'failed' | 'done';
  }[] = [];
  public compensateForOwned: any[] = [];

  public googleKey = '';
  public totalImageCount = 10;
  public totalImageCountThatSysAdminSet = 10;
  public useFirstImgAsCover = true;
  public selectedImageFiles: File[];

  constructor(
    private dialogRef: MatDialogRef<EditPlaceImagesModalComponent>,
    private actions: EditPlaceImagesModalActions,
    private hubApiService: HubApiService,
    public imageService: ImageService,
    private alertify: AlertifyService,
    private modalService: ModalService,
    private toggleAndDataSourceStore: ToggleAndDataSourceStore,
    private formBuilder: FormBuilder,
    data: HubPlace
  ) {
    super();
    this.place = Object.assign({}, data);
    this.hubName = this.place.hubName;

    this.toggleAndDataSourceStore.toggleAndDataSourceCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command: HubVisitorToggleDataSource) => {
        if (command && command.dataSource.placeImagesShownCount) {
          this.totalImageCountThatSysAdminSet = command.dataSource.placeImagesShownCount;
        }

        this.getGooglePlaceAdditionalData();
      });
  }

  public async getGooglePlaceAdditionalData() {
    if (this.place.socialName !== 'google') {
      return;
    }

    if (
      isEmptyArray(this.place.allowedGooglePhotos) ||
      this.place.allowedGooglePhotos.any((x) => !x.includes('amazonaws')) ||
      (!isEmptyArray(this.place.blockedGooglePhotos) &&
        this.place.blockedGooglePhotos.any((x) => !x.includes('amazonaws')))
    ) {
      return await this.updateImages(false);
    }

    this.buildImages();
    this.compensate();
    this.isGoogleInfoLoaded = true;
  }

  public async confirm(event: ButtonTriggerEvent) {
    if (!this.publishedImages.length) {
      event.immediateComplete();
      this.alertify.warning('Please select at least one allowed image');
      return;
    }

    if (!this.place.cover) this.place.cover = { source: '', isDefault: false };

    // // if the cover image of this place is not uploaded by user, then use the first allowed image as cover
    // if (!this.place.cover.source.includes('iti-user-upload/')) {
    //   this.place.cover.source = this.publishedImages[0];
    // } else
    // {
    //   // if the cover image is from a one of the client uploaded own images in this modal, then it should be covered by the first allowed image
    //   if (
    //     this.publishedImages.any((x) => x === this.place.cover.source) ||
    //     this.availableImages.any((x) => x === this.place.cover.source) ||
    //     this.ownedImages.any((x) => x.url === this.place.cover.source)
    //   ) {
    //     this.place.cover.source = this.publishedImages[0];
    //   }
    // }

    // now use the first image from publishedImages as the cover image, as the cover image is now put in the publishedImages initially
    this.coverImage = this.publishedImages[0];

    // for client uploaded cover image, it will be re-added in buildImages() when opening this popup
    if (this.isClientUploaded(this.publishedImages[0])) {
      this.publishedImages.shift();
    }

    this.place.allowedGooglePhotos = this.publishedImages;
    this.place.blockedGooglePhotos = this.availableImages;

    await this.uploadOwnedImagesToAWS();

    if (this.ownedImages && this.ownedImages.any((img) => img.status === 'failed')) {
      event.immediateComplete();
      return;
    }

    this.place.ownedPhotos = this.ownedImages.map((x) => x.url);

    // to let users see the last image uploaded successfully
    this.ownedImages.any((x) => x.status === 'uploaded') && (await this.myWait(1000));
    event.immediateComplete();

    this.actions.confirm(event, this.place, this.coverImage);
    this.dialogRef.close();
  }

  public async uploadOwnedImagesToAWS() {
    for (const img of this.ownedImages) {
      if (img.url.includes('amazonaws')) continue;

      img.status = 'uploading';
      const res = await this.hubApiService.uploadBase64ImageToAWS(
        img.url,
        config.compressImgWidth,
        'places/iti-user-upload/'
      );
      if (res.success) {
        img.url = res.url;
        img.status = 'uploaded';
        setTimeout(() => {
          img.status = 'done';
        }, 3000);
      } else {
        img.status = 'failed';
      }
    }
  }

  public removeOwnImage(index: number) {
    this.ownedImages.splice(index, 1);
  }

  public getImage(imgUrl: string, isRealReference = true) {
    if (imgUrl.startsWith('http') || imgUrl.startsWith('data:')) {
      return imgUrl;
    }

    let url = PlaceService.getGooglePlaceImageFullUrl(imgUrl, this.googleKey, isRealReference);
    return this.imageService.imageSourceUrl(url);
  }

  public dragEnter($event) {
    if ($event && $event.currentTarget) {
      this.restoreItemStyle();
      $event.currentTarget.style.border = '2px dashed green';
      if (!this.dropTargets.any((x) => x === $event.currentTarget)) {
        this.dropTargets.push($event.currentTarget);
      }
    }
  }

  public async startDrag($event, from: string) {
    if (this.isWaitingForUpload($event.srcElement.currentSrc)) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.dragFrom = from;
    }
  }

  public async onItemDrop(e: any, to: string, index: number = -1) {
    this.restoreItemStyle();

    if (this.dragFrom === 'owned') {
      if (this.ownedImages.any((x) => this.isWaitingForUpload(x.url))) {
        this.alertify.warning('Please wait for uploading, and move the image again.');
        await this.uploadOwnedImagesToAWS();
        this.isDragging = false;
        return;
      }
    }

    if (e.dragData) {
      let photoReference = PlaceService.getGooglePlaceReference(e.dragData);

      // drag from allowed list to blocked list
      if (this.dragFrom === 'allowed' && to === 'blocked') {
        // client uploaded image or just selected image (not uploaded yet) won't be able to move to blocked/available list
        if (this.isClientUploaded(photoReference)) {
          this.removeOwnedImageFromAllowed(photoReference);
          if (!this.ownedImages.any(x => x.url === photoReference)) {
            this.ownedImages.push({ url: photoReference, status: 'initial' });
          }
        } else {
          this.publishedImages = this.publishedImages.filter((x) => x !== photoReference);
          if (!this.availableImages.any((x) => x === photoReference)) {
            this.availableImages.push(photoReference);
          }
        }
      }

      // drag from blocked list to allowed list
      if (this.dragFrom === 'blocked' && to === 'allowed') {
        if (!this.publishedImages.any((x) => x === photoReference)) {
          let dragDataIndex = this.availableImages.indexOf(photoReference);
          // replace an allowed image
          if (index >= 0) {
            let temp = this.publishedImages[index];
            this.publishedImages[index] = photoReference;
            !this.isClientUploaded(temp) &&
              !this.isWaitingForUpload(temp) &&
              (this.availableImages[dragDataIndex] = temp);
          } else {
            // add to the end of the allowed list
            this.publishedImages.push(photoReference);
          }
          this.availableImages = this.availableImages.filter((x) => x !== photoReference);
        }
      }

      if (this.dragFrom === 'allowed' && to === 'allowed') {
        if (index === -1) {
          this.isDragging = false;
          return;
        }
        let dragDataIndex = this.publishedImages.indexOf(photoReference);
        let temp = this.publishedImages[index];
        this.publishedImages[index] = photoReference;
        this.publishedImages[dragDataIndex] = temp;
      }

      if (this.dragFrom === 'owned' && to === 'allowed') {
        if (!this.publishedImages.any((x) => x === photoReference)) {
          // replace an allowed image
          if (index >= 0 && index <= this.publishedImages.length) {
            let temp = this.publishedImages[index];
            this.publishedImages[index] = photoReference;
            !this.isClientUploaded(temp) && !this.isWaitingForUpload(temp) && this.availableImages.push(temp);
          } else {
            // add to the end of the allowed list
            this.publishedImages.push(photoReference);
          }
        } else {
          this.alertify.warning('This image is already added');
        }
      }

      if (this.dragFrom === 'owned' && to === 'blocked') {
        this.alertify.warning(`You can't add this image to this list`);
      }

      this.compensate();
    }
    this.isDragging = false;
  }

  public removeOwnedImageFromAllowed(image: string) {
    this.publishedImages = this.publishedImages.filter((x) => x !== image);
    this.compensate();
  }

  public isClientUploaded(image: string) {
    return image.includes('iti-user-upload');
  }

  public isWaitingForUpload(image: string) {
    if (!image) return false;
    return !image.includes('amazonaws');
  }

  public close(): void {
    if (this.actions.cancel) {
      this.actions.cancel();
    }

    this.dialogRef.close();
  }

  public async updateImages(isAskForConfirm = true) {
    const run = async () => {
      this.isGoogleInfoLoaded = false;
      this.googlePlaceAdditionalData = await PlaceService.getGooglePlaceDetails(
        this.place,
        this.hubName,
        this.hubApiService,
        true
      );
      if (!this.googlePlaceAdditionalData) {
        this.alertify.warning('This Google place has no photos');
        this.close();
      }
      if (this.googlePlaceAdditionalData.allowedGooglePhotos) {
        let allPhotos = this.googlePlaceAdditionalData.allowedGooglePhotos;
        this.googleKey = this.googlePlaceAdditionalData.appKey;

        let realReferences = allPhotos.map((x) => PlaceService.getGooglePlaceReference(x));
        this.place.allowedGooglePhotos = realReferences;
        this.place.blockedGooglePhotos = isAskForConfirm ? [] : this.googlePlaceAdditionalData.blockedGooglePhotos;
      }

      if (this.place.cover && this.place.cover.source) {
        this.place.cover.source = this.imageService.imageSource(this.place);
      }

      this.buildImages();
      this.compensate();
      this.isGoogleInfoLoaded = true;
    };

    if (isAskForConfirm) {
      let actions = <ConfirmModalActions>{
        confirm: async (event: ButtonTriggerEvent) => {
          event.complete();
          await run();
        },
      };
      let data = <ConfirmModalData>{
        confirmModalIcon: 'warning',
        confirmModalTitle: 'Alert',
        confirmModalDescription:
          'Are you sure you want to update Google images of this place, this will refresh all Google images and reset them to allowed status?',
        confirmModalConfirmButtonText: 'Yes',
        confirmModalCancelButtonText: 'Cancel',
        isAsync: true,
      };

      this.modalService.show({
        component: ConfirmModalComponent,
        providers: [
          { provide: ConfirmModalActions, useValue: actions },
          { provide: ConfirmModalData, useValue: data },
        ],
      });
    } else {
      await run();
    }
  }

  public enterImageLink() {
    let actions = <InputModalActions>{
      confirm: async (event: ButtonTriggerEvent, url) => {
        console.log(url);
        this.ownedImages.push({
          url,
          status: 'initial',
        });
        await this.uploadOwnedImagesToAWS();
      },
    };

    this.modalService.show({
      component: InputModalComponent,
      providers: [
        { provide: InputModalActions, useValue: actions },
        {
          provide: InputModalData,
          useValue: new InputModalData(
            'Enter image link',
            '',
            null,
            'OK',
            'CANCEL',
            false
          ),
        },
      ],
      panelClass: 'width-modal-2',
    });
  }

  public async onImageChange($event) {
    if ($event.srcElement.files.length > 10 || $event.srcElement.files.length + this.ownedImages.length > 10) {
      this.alertify.warning(`You can't upload more than 10 images`);
    }

    this.selectedImageFiles = $event.srcElement.files;

    if (this.selectedImageFiles && this.selectedImageFiles.length > 0) {
      for (let i = 0; i < this.selectedImageFiles.length; i++) {
        if (this.ownedImages.length >= 10) break;

        const selectedImageFiles = await this.imageService.compressImageFileToBase64(this.selectedImageFiles[i]);
        this.ownedImages.push({
          url: selectedImageFiles,
          status: 'initial',
        });
      }

      await this.uploadOwnedImagesToAWS();
    }
    // reset file input
    $event.srcElement.value = '';
  }

  private restoreItemStyle() {
    this.dropTargets.forEach((x) => {
      x.style.border = 'none';
    });
  }

  private buildImages() {
    this.publishedImages = [];
    this.availableImages = [];
    this.ownedImages = [];
    let hasUserUploadedCoverImage = false;
    let allowedPhotos = clone(this.place.allowedGooglePhotos) || [];

    if (this.place.cover.source) {
      // insert the client uploaded cover image as the first one of the allowed images
      // replace the first one with the cover image if the first one was not client uploaded
      // so that the cover image will always show as the first one in allowed images, and there is no duplicated image issue
      if (this.isClientUploaded(this.place.cover.source)) {
        this.publishedImages.push(this.place.cover.source);
        hasUserUploadedCoverImage = true;
      } else {
        allowedPhotos[0] = this.place.cover.source;
      }
    }

    if (this.place.allowedGooglePhotos && this.place.allowedGooglePhotos.length >= 0) {
      let images = [...this.publishedImages, ...allowedPhotos];
      this.publishedImages = images.slice(0, this.totalImageCountThatSysAdminSet);
      this.availableImages = allowedPhotos
        .slice(hasUserUploadedCoverImage ? this.totalImageCountThatSysAdminSet - 1 : this.totalImageCountThatSysAdminSet)
        .concat(this.place.blockedGooglePhotos);
    }

    if (this.place.ownedPhotos && this.place.ownedPhotos.length >= 0) {
      this.ownedImages = this.place.ownedPhotos.map((x) => {
        return { url: x, status: 'initial' as any };
      });
    }
  }

  // allowed row and blocked row both show 10 images in a flex div, so here to compensate to make them as 10 if dragged out one
  private compensate() {
    if (this.publishedImages && this.publishedImages.length >= 0) {
      this.compensateForPublished = [];
      for (let i = 0; i < this.totalImageCountThatSysAdminSet - this.publishedImages.length; i++) {
        this.compensateForPublished.push({});
      }
    }

    if (this.availableImages && this.availableImages.length >= 0) {
      this.compensateForAvailable = [];
      for (let i = 0; i < this.totalImageCount - this.availableImages.length; i++) {
        this.compensateForAvailable.push({});
      }
    }

    // if (this.ownedImages && this.ownedImages.length >= 0) {
    //   this.compensateForOwned = [];
    //   for (let i = 0; i < this.totalImageCount - this.ownedImages.length; i++) {
    //     this.compensateForOwned.push({});
    //   }
    // }
  }

  private myWait(milSeconds) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(null);
      }, milSeconds);
    });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/edit.place.images.modal/edit.place.images.modal.component.ts